import { SIX_TOPIC } from '@/config/networkConstants';
import type { FileMetadata } from '@/types/poc/helia';
import type { MessagePayload } from '@/types/poc/message';
import { MAX_RESEND_ATTEMPTS } from '@/store/listeners/poc/pendingAckCoordinator';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';

interface AnnounceFileKeyReadyArgs {
  sixId: string;
  fileId: string;
  newFileKeyCID: string;
  newFileKeyForUserId: string;
  newFileKeyCreatorUserId: string;
  metadata: FileMetadata;
  attemptCount?: number;
}

interface AnnounceFileKeyReadyResult {
  sixId: string;
  fileId: string;
  newFileKeyCID: string;
  messageId: string;
  attemptCount: number;
}

export const announceFileKeyReadyThunk = createAppAsyncThunk<
  AnnounceFileKeyReadyResult,
  AnnounceFileKeyReadyArgs
>(
  'messaging/announceFileKeyReady',
  async(
    {
      sixId,
      fileId,
      newFileKeyCID,
      newFileKeyForUserId,
      newFileKeyCreatorUserId,
      metadata,
      attemptCount = 1,
    },
    { dispatch, extra: { pocServices, loggerService }, rejectWithValue }
  ) => {
    try {
      const log = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
      const { p2pStackManagerService, messagingService } = pocServices;
      const libp2pNode = p2pStackManagerService.getLibp2pNode(sixId);

      if (libp2pNode == null) {
        throw new Error(`LibP2P node not available for six '${sixId}'`);
      }

      const message: MessagePayload = {
        type: 'file-key-ready-for-download',
        id: crypto.randomUUID(),
        fileId,
        newFileKeyCID,
        newFileKeyForUserId,
        newFileKeyCreatorUserId,
        sixId,
        sender: libp2pNode.peerId.toString(),
        timestamp: Date.now(),
      };

      await messagingService.publishMessage(libp2pNode, SIX_TOPIC(sixId), message);

      log.info(
        `[${sixId}] File key ready message ${message.id} sent (attempt ${attemptCount}/${MAX_RESEND_ATTEMPTS}) for file: ${fileId}`
      );

      return { sixId, fileId, newFileKeyCID, messageId: message.id, attemptCount };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ message });
    }
  }
);
